/**
 * PublicRouterPage — the one question at /r/:slug that puts a booker on the
 * right service's page.
 *
 * A salon publishes ONE link. The person clicking it does not know whether
 * they want the 30-minute cut or the 90-minute colour, and those are separate
 * booking pages with their own duration, price and availability. Publishing
 * five links instead just moves that problem onto the merchant.
 *
 * Same shape as `PublicBookingPage` deliberately: no app chrome, no auth, one
 * fetch, and a plain anchor per option so the choice is a normal navigation —
 * the booker can middle-click it, and it keeps working if the script fails.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { t } from '../../i18n';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.buzzbitx.com';

interface RouterOption {
  label: string;
  slug: string;
  title: string;
  durationMin: number;
}

interface PublicRouter {
  title: string;
  question: string;
  options: RouterOption[];
}

export default function PublicRouterPage(): React.ReactElement {
  const { slug = '' } = useParams();
  const [data, setData] = useState<PublicRouter | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API_URL}/api/booking-page/router/${encodeURIComponent(slug)}`);
        const json = (await res.json()) as { success?: boolean; data?: PublicRouter };
        if (cancelled) return;
        if (!res.ok || json.data === undefined) {
          setLoadError(t('router.gone'));
          return;
        }
        setData(json.data);
      } catch {
        if (!cancelled) setLoadError(t('router.loadFailed'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loadError !== null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <p className="text-gray-600 text-sm">{loadError}</p>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <p className="text-gray-400 text-sm">{t('router.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="mx-auto max-w-md">
        <h1 className="text-xl font-semibold text-gray-900">{data.title}</h1>
        <p className="mt-1 text-sm text-gray-500">{data.question}</p>

        {data.options.length === 0 ? (
          /* Every option pointed at an archived service. Saying so beats an
             empty page the booker reads as broken. */
          <p className="mt-6 text-sm text-gray-500">{t('router.nothingBookable')}</p>
        ) : (
          <ul className="mt-6 space-y-3">
            {data.options.map((o) => (
              <li key={o.slug}>
                <a
                  href={`/book/${o.slug}`}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left hover:border-gray-400"
                >
                  <span>
                    <span className="block text-sm font-medium text-gray-900">{o.label}</span>
                    <span className="block text-xs text-gray-500">{o.title}</span>
                  </span>
                  <span className="text-xs text-gray-500">{t('router.minutes', { n: String(o.durationMin) })}</span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
